/** StatCard (§16.2 tiles): real numbers only; `value === null` renders an honest "No data yet". */
import { Skeleton } from '@windrop/ui';
import type { ReactNode } from 'react';
import { NoDataYet } from './EmptyState';
import { ToneBadge, type Tone } from './StatusBadge';

export interface StatCardProps {
  label: string;
  /** Formatted value; null = nothing reported yet. */
  value: ReactNode | null;
  /** Small text under the value (period, unit, denominator). */
  hint?: ReactNode;
  /** Secondary signal with its own text (e.g. "3 overdue"). */
  signal?: { tone: Tone; text: string };
  loading?: boolean;
  /** Link or button to the console that owns this number. */
  action?: ReactNode;
  /** What is missing when there is no data — keeps the tile honest. */
  emptyHint?: string;
}

export function StatCard({ label, value, hint, signal, loading, action, emptyHint }: StatCardProps) {
  return (
    <section className="wdc-stat wd-surface" aria-busy={loading || undefined}>
      <h3 className="wdc-stat__label">{label}</h3>
      {loading ? (
        <Skeleton shape="display" width="60%" label={`Loading ${label}`} />
      ) : value === null ? (
        <NoDataYet what={label.toLowerCase()} hint={emptyHint} />
      ) : (
        <p className="wdc-stat__value wd-tabular">{value}</p>
      )}
      {hint && !loading && value !== null ? <p className="wdc-stat__hint wd-muted">{hint}</p> : null}
      {signal && !loading ? <ToneBadge tone={signal.tone}>{signal.text}</ToneBadge> : null}
      {action ? <div className="wdc-stat__action">{action}</div> : null}
    </section>
  );
}
